// Mobile-optimized camera capture + gallery picker with automatic compression

import { useState, useRef } from 'react';
import { compressImage } from '../shared/ImageCompressor';

export interface CapturedPhoto {
  id: string;
  file: File;
  previewUrl: string;
  uploading: boolean;
  uploaded: boolean;
  photoUrl?: string;
  error?: string;
}

interface Props {
  stepType: 'pickup' | 'return' | 'carrier_load' | 'carrier_unload';
  reservationId: string;
  onUpload: (photo: CapturedPhoto) => void;
  multiple?: boolean;
}

export default function InspectionCamera({ stepType, reservationId, onUpload, multiple = true }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);

    // Create preview entries
    const entries: CapturedPhoto[] = fileArray.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      uploading: false,
      uploaded: false,
    }));

    setPhotos(prev => [...prev, ...entries]);

    // Compress and upload each photo
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      setOverallProgress(Math.round(((i) / entries.length) * 100));

      try {
        setPhotos(prev => prev.map(p => p.id === entry.id ? { ...p, uploading: true } : p));

        const compressed = await compressImage(entry.file, { maxWidth: 1920, quality: 0.75, outputType: 'webp' });
        const formData = new FormData();
        formData.append('file', compressed, entry.file.name.replace(/\.[^.]+$/, '.webp'));
        formData.append('step_type', stepType);
        formData.append('photo_category', 'general');

        // Import api dynamically to avoid circular dep
        const { reservationApi } = await import('../../lib/api');
        const result: any = await reservationApi.uploadInspectionPhoto(reservationId, formData);

        setPhotos(prev => prev.map(p => p.id === entry.id ? { ...p, uploading: false, uploaded: true, photoUrl: result.photo_url } : p));
        onUpload(entry);
      } catch (err: any) {
        setPhotos(prev => prev.map(p => p.id === entry.id ? { ...p, uploading: false, error: err.message || 'Upload failed' } : p));
      }
    }

    setOverallProgress(100);
  };

  const removePhoto = (id: string) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };

  const totalUploading = photos.filter(p => p.uploading).length;
  const totalDone = photos.filter(p => p.uploaded).length;
  const totalFailed = photos.filter(p => p.error).length;

  return (
    <div className="space-y-4">
      {/* Action buttons — large touch targets for mobile */}
      <div className="flex gap-3">
        <button
          onClick={() => cameraInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white py-4 rounded-2xl font-medium text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          카메라 촬영
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-gray-200 hover:border-gray-300 text-slate-700 py-4 rounded-2xl font-medium text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          갤러리에서 선택
        </button>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={multiple}
        onChange={e => processFiles(e.target.files)}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={e => processFiles(e.target.files)}
        className="hidden"
      />

      {/* Progress bar */}
      {totalUploading > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
            <span>업로드 중... ({totalDone}/{photos.length})</span>
            <span>{overallProgress}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-300"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map(p => (
            <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 group">
              <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
              {/* Status overlay */}
              {p.uploading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                </div>
              )}
              {p.uploaded && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {p.error && (
                <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                  <span className="text-xs text-red-600 bg-white/90 px-2 py-1 rounded-lg">{p.error}</span>
                </div>
              )}
              {/* Remove button */}
              <button
                onClick={() => removePhoto(p.id)}
                className="absolute top-1.5 left-1.5 w-5 h-5 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:opacity-100"
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {photos.length > 0 && totalUploading === 0 && (
        <p className="text-xs text-gray-400 text-center">
          {totalDone}장 업로드 완료
          {totalFailed > 0 && ` · ${totalFailed}장 실패`}
        </p>
      )}
    </div>
  );
}
